import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useGetClientQuery, useTestClientConnectionMutation, useLockClientMutation, useActivateClientMutation } from '../../store/api/clientsApi';
import { useGetProvidersQuery } from '../../store/api/providersApi';
import { AuthType } from '../../types/api';
import { useNotifications } from '../../hooks/useNotifications';
import { ClientEditModal } from './ClientEditModal';
import { 
  Modal, 
  ModalOverlay, 
  ModalContent, 
  ModalHeader, 
  ModalTitle, 
  ModalBody,
  ModalFooter,
  CloseButton,
  Button,
  Badge,
  LoadingSpinner,
  Alert
} from '../../styles/components';
import {
  XMarkIcon,
  ServerIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  BuildingLibraryIcon,
  DocumentMagnifyingGlassIcon,
  UserIcon
} from '@heroicons/react/24/outline';

const DetailSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const SectionTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.lg};
  font-weight: ${props => props.theme.fontWeights.semibold};
  color: ${props => props.theme.colors.gray[900]};
  margin-bottom: ${props => props.theme.spacing.md};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: ${props => props.theme.colors.primary[600]};
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${props => props.theme.spacing.md};
  
  @media (min-width: 640px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const DetailItem = styled.div``;

const DetailLabel = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.theme.colors.gray[500]};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const DetailValue = styled.div`
  font-size: ${props => props.theme.fontSizes.base};
  color: ${props => props.theme.colors.gray[900]};
  word-break: break-all;
`;

const DetailValueCode = styled(DetailValue)`
  font-family: monospace;
  background-color: ${props => props.theme.colors.gray[100]};
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borders.radius.sm};
  font-size: ${props => props.theme.fontSizes.sm};
`;

const IPList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const IPAddress = styled.div`
  font-family: monospace;
  font-size: ${props => props.theme.fontSizes.sm};
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  background-color: ${props => props.theme.colors.gray[100]};
  border-radius: ${props => props.theme.borders.radius.sm};
  display: inline-block;
`;

const ActionButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  
  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const DangerButton = styled(ActionButton)`
  background-color: ${props => props.theme.colors.error[600]};
  
  &:hover {
    background-color: ${props => props.theme.colors.error[700]};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing['2xl']};
`;

const ErrorContainer = styled.div`
  padding: ${props => props.theme.spacing.lg};
`;

interface ClientDetailModalProps {
  clientId: string;
  onClose: () => void;
}

const STATUS_MAP: Record<string, string> = {
  '0': 'Created',
  '2': 'Active',
  '3': 'Locked',
  '4': 'ActivationFailed',
  'Created': 'Created',
  'Active': 'Active',
  'Locked': 'Locked',
  'ActivationFailed': 'ActivationFailed',
};

const getStatusVariant = (status: string): 'success' | 'error' | 'warning' | 'info' => {
  switch (status) {
    case 'Active':
      return 'success';
    case 'Locked':
    case 'ActivationFailed':
      return 'error';
    case 'Created':
      return 'warning';
    default:
      return 'info';
  }
};

export function ClientDetailModal({ clientId, onClose }: ClientDetailModalProps) {
  const navigate = useNavigate();
  const { notify } = useNotifications();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showLockConfirm, setShowLockConfirm] = useState(false);
  const [showActivateConfirm, setShowActivateConfirm] = useState(false);
  
  const { 
    data: client, 
    isLoading: clientLoading, 
    error: clientError 
  } = useGetClientQuery(clientId);

  const { data: providers } = useGetProvidersQuery();

  const [testConnection, { isLoading: testingConnection }] = useTestClientConnectionMutation();
  const [lockClient, { isLoading: lockingClient }] = useLockClientMutation();
  const [activateClient, { isLoading: activatingClient }] = useActivateClientMutation();

  const handleTestConnection = async () => {
    if (!client) return;
    
    try {
      const result = await testConnection(client.clientId).unwrap();
      if (result.success) {
        notify.success('Connection test successful', 'Bank connection is working correctly.');
      } else {
        notify.error('Connection test failed', result.error?.errorMsg || 'Unknown error occurred.');
      }
    } catch (error: any) {
      notify.error('Connection test failed', error?.data?.errorMsg || 'Please check your configuration and try again.');
    }
  };

  const handleViewLogs = () => {
    if (!client) return;
    onClose(); // Close modal first
    navigate(`/logs?clientId=${client.clientId}`);
  };

  const handleLockClient = async () => {
    if (!client) return;
    
    try {
      await lockClient(client.clientId).unwrap();
      notify.success('Client locked', 'The client has been locked successfully.');
      setShowLockConfirm(false);
    } catch (error: any) {
      notify.error('Failed to lock client', error?.data?.message || 'Please try again or contact support.');
    }
  };

  const handleActivateClient = async () => {
    if (!client) return;
    
    try {
      await activateClient(client.clientId).unwrap();
      notify.success('Client activated', 'The client has been activated successfully.');
      setShowActivateConfirm(false);
    } catch (error: any) {
      notify.error('Failed to activate client', error?.data?.message || 'Please try again or contact support.');
    }
  };


  if (clientLoading && !client) {
    return (
      <Modal $isOpen>
        <ModalOverlay onClick={onClose} />
        <ModalContent>
          <LoadingContainer>
            <LoadingSpinner />
          </LoadingContainer>
        </ModalContent>
      </Modal>
    );
  }

  if (!client) {
    return null;
  }

  // Show edit modal instead of detail modal when editing
  if (showEditModal) {
    return (
      <ClientEditModal
        clientId={clientId}
        onClose={() => setShowEditModal(false)}
        onClientDeleted={onClose}
      />
    );
  }

  const status = STATUS_MAP[client.status] || client.status;
  const currentProvider = providers?.find(p => p.name === client.providerName);
  const authType = currentProvider?.authType;

  return (
    <Modal $isOpen>
      <ModalOverlay onClick={onClose} />
      <ModalContent style={{ maxWidth: '800px' }}>
        <ModalHeader>
          <ModalTitle>Client Details</ModalTitle>
          <ActionButton
            onClick={() => setShowEditModal(true)}
            style={{ backgroundColor: '#3b82f6' }}
          >
            <PencilIcon style={{ width: '1rem', height: '1rem' }} />
            Edit
          </ActionButton>
        </ModalHeader>
        
        <ModalBody>
          <DetailSection>
            <SectionTitle>
              <DocumentTextIcon />
              Basic Information
            </SectionTitle>
            <DetailGrid>
              <DetailItem>
                <DetailLabel>Client ID</DetailLabel>
                <DetailValueCode>{client.clientId}</DetailValueCode>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Status</DetailLabel>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <Badge variant={getStatusVariant(status)}>
                    {status}
                  </Badge>
                  {status === 'Active' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowLockConfirm(true)}
                      disabled={lockingClient}
                      style={{ 
                        borderColor: '#dc2626', 
                        color: '#dc2626',
                        fontSize: '0.75rem',
                        padding: '0.25rem 0.5rem'
                      }}
                    >
                      {lockingClient ? 'Locking...' : 'Lock Client'}
                    </Button>
                  )}
                  {(status === 'Created' || status === 'Locked' || status === 'ActivationFailed') && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowActivateConfirm(true)}
                      disabled={activatingClient}
                      style={{ 
                        borderColor: '#16a34a', 
                        color: '#16a34a',
                        fontSize: '0.75rem',
                        padding: '0.25rem 0.5rem'
                      }}
                    >
                      {activatingClient ? 'Activating...' : 'Activate Client'}
                    </Button>
                  )}
                </div>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Organization ID</DetailLabel>
                <DetailValue>{client.organizationId}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Organization Name</DetailLabel>
                <DetailValue>{client.organizationName || 'Not set'}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Created</DetailLabel>
                <DetailValue>
                  {new Date(client.created).toLocaleDateString()} at{' '}
                  {new Date(client.created).toLocaleTimeString()}
                </DetailValue>
              </DetailItem>
              {client.createdBy && (
                <DetailItem>
                  <DetailLabel>Created By</DetailLabel>
                  <DetailValue>{client.createdBy}</DetailValue>
                </DetailItem>
              )}
              {client.modified && (
                <DetailItem>
                  <DetailLabel>Last Modified</DetailLabel>
                  <DetailValue>
                    {new Date(client.modified).toLocaleDateString()} at{' '}
                    {new Date(client.modified).toLocaleTimeString()}
                  </DetailValue>
                </DetailItem>
              )}
              {client.modifiedBy && (
                <DetailItem>
                  <DetailLabel>Modified By</DetailLabel>
                  <DetailValue>{client.modifiedBy}</DetailValue>
                </DetailItem>
              )}
              {client.lastLogin && (
                <DetailItem>
                  <DetailLabel>Last Login</DetailLabel>
                  <DetailValue>
                    {new Date(client.lastLogin).toLocaleDateString()} at{' '}
                    {new Date(client.lastLogin).toLocaleTimeString()}
                  </DetailValue>
                </DetailItem>
              )}
            </DetailGrid>
          </DetailSection>

          <DetailSection>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <SectionTitle style={{ marginBottom: '0.5rem' }}>
                  <BuildingLibraryIcon />
                  Financial Institution
                </SectionTitle>
                <DetailLabel>Institution</DetailLabel>
                <DetailValue>{client.providerName || 'No financial institution assigned'}</DetailValue>
              </div>
              <div>
                <SectionTitle style={{ marginBottom: '0.5rem' }}>
                  <UserIcon />
                  Credentials
                </SectionTitle>
                {authType === AuthType.CustomerIdSignature ? (
                  <>
                    <DetailLabel>Customer ID</DetailLabel>
                    <DetailValue>{client.customerId || 'Not set'}</DetailValue>
                  </>
                ) : (
                  <>
                    <DetailLabel>Username</DetailLabel>
                    <DetailValue>{client.username || 'Not set'}</DetailValue>
                  </>
                )}
              </div>
            </div>
          </DetailSection>

          <DetailSection>
            <SectionTitle>
              <ShieldCheckIcon />
              Certificate Information
            </SectionTitle>
            <DetailGrid>
              <DetailItem>
                <DetailLabel>Certificate Serial Number</DetailLabel>
                {client.certificateSerialNumber ? (
                  <DetailValueCode>{client.certificateSerialNumber}</DetailValueCode>
                ) : (
                  <DetailValue>Not set</DetailValue>
                )}
              </DetailItem>
              <DetailItem>
                <DetailLabel>Certificate Expiration Date</DetailLabel>
                <DetailValue>
                  {client.certificateExpirationDate
                    ? new Date(client.certificateExpirationDate).toLocaleDateString()
                    : 'Not set'}
                </DetailValue>
              </DetailItem>
            </DetailGrid>
          </DetailSection>



          {client.clientIPs && client.clientIPs.length > 0 && (
            <DetailSection>
              <SectionTitle>
                <ServerIcon />
                Allowed IP Addresses
              </SectionTitle>
              <IPList>
                {client.clientIPs.map((ip, index) => (
                  <IPAddress key={index}>{ip}</IPAddress>
                ))}
              </IPList>
            </DetailSection>
          )}


          {/* Lock Client Confirmation */}
          {showLockConfirm && (
            <Alert variant="warning" style={{ marginTop: '1.5rem' }}>
              <ExclamationTriangleIcon style={{ width: '1rem', height: '1rem' }} />
              <div>
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: 'bold' }}>Lock Client Confirmation</p>
                <p style={{ margin: '0 0 1rem 0' }}>
                  This will immediately lock the client and prevent all API access. Are you sure you want to continue?
                </p>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <DangerButton 
                    onClick={handleLockClient}
                    disabled={lockingClient}
                    size="sm"
                  >
                    {lockingClient ? 'Locking...' : 'Confirm Lock'}
                  </DangerButton>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowLockConfirm(false)}
                    disabled={lockingClient}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </Alert>
          )}

          {/* Activate Client Confirmation */}
          {showActivateConfirm && (
            <Alert variant="info" style={{ marginTop: '1.5rem' }}>
              <div>
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: 'bold' }}>Activate Client Confirmation</p>
                <p style={{ margin: '0 0 1rem 0' }}>
                  This will activate the client and allow API access. Are you sure you want to continue?
                </p>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <ActionButton 
                    onClick={handleActivateClient}
                    disabled={activatingClient}
                    size="sm"
                    style={{ backgroundColor: '#16a34a' }}
                  >
                    {activatingClient ? 'Activating...' : 'Confirm Activate'}
                  </ActionButton>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowActivateConfirm(false)}
                    disabled={activatingClient}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </Alert>
          )}

        </ModalBody>
        
        <ModalFooter>
          <ActionButton
            onClick={handleTestConnection}
            disabled={testingConnection || status !== 'Active' || !client.providerName}
            style={{ 
              backgroundColor: (status === 'Active' && client.providerName && !testingConnection) ? '#10b981' : '#9ca3af' 
            }}
            title={
              status !== 'Active' 
                ? 'Client must be active to test connection' 
                : !client.providerName 
                  ? 'A financial institution must be assigned to test connection' 
                  : ''
            }
          >
            <ServerIcon style={{ width: '1rem', height: '1rem' }} />
            {testingConnection ? 'Testing Connection...' : 'Test Bank Connection'}
          </ActionButton>
          
          <ActionButton
            onClick={handleViewLogs}
            style={{ 
              backgroundColor: '#6366f1' 
            }}
          >
            <DocumentMagnifyingGlassIcon style={{ width: '1rem', height: '1rem' }} />
            View Logs
          </ActionButton>
          
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}